import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, Send, Truck, FileDown, Loader2, Ban, Pencil, Save, Zap, FileText, Plus, Search, FileSpreadsheet, Package } from 'lucide-react';
import { API_BASE } from '@/lib/api';
import InvoiceDialog from './InvoiceDialog';

const STATUS_LABELS = {
  draft: 'Draft', pending_approval: 'Pending your approval',
  changes_requested: 'Changes requested', approved: 'Approved',
  submitted: 'Submitted to Doc Johnson', fulfilled: 'Fulfilled',
  rejected: 'Rejected', cancelled: 'Cancelled',
};

function StatusPill({ status }) {
  const cls = `badge badge--${status || 'submitted'}`;
  return <span className={cls}>{STATUS_LABELS[status] || status}</span>;
}

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [changeReason, setChangeReason] = useState('');
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [vendorPOs, setVendorPOs] = useState(null);

  useEffect(() => {
    if (!editMode || !addQuery.trim()) { setAddResults([]); return; }
    const t = setTimeout(async () => {
      setAddSearching(true);
      try {
        const { data } = await api.get('/products', { params: { q: addQuery.trim(), limit: 12 } });
        setAddResults(data.items || []);
      } catch { setAddResults([]); } finally { setAddSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [addQuery, editMode]);

  const addLine = (p) => {
    setEditItems((cur) => {
      const existing = cur.find((i) => i.sku === p.sku);
      if (existing) return cur.map((i) => i.sku === p.sku ? { ...i, qty: i.qty + 1 } : i);
      return [...cur, { sku: p.sku, name: p.name, brand: p.brand, qty: p.min_order_qty || 1 }];
    });
  };

  const loadInvoices = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(`/admin/orders/${id}/invoices`);
      setInvoices(data || []);
    } catch { /* silent */ }
  };

  const loadVendorPOs = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(`/admin/orders/${id}/vendor-pos`);
      setVendorPOs(data);
    } catch { setVendorPOs(null); }
  };

  const downloadVendorPO = async (vendor, ext) => {
    try {
      const safe = encodeURIComponent(vendor);
      const resp = await api.get(`/admin/orders/${id}/vendor-po/${safe}.${ext}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const safeVendor = vendor.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
      a.download = `${order.order_number}-${safeVendor}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch { toast.error(`Could not download ${ext.toUpperCase()}`); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      if (e.response?.status === 404) toast.error('Order not found');
      else if (e.response?.status === 403) toast.error('You cannot view that order');
      else toast.error('Failed to load order');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (order && user?.role === 'admin') { loadInvoices(); loadVendorPOs(); } /* eslint-disable-line */ }, [order?.id, user?.role]);

  const isAdmin = user?.role === 'admin';
  const canInvoice = isAdmin && ['approved', 'submitted', 'fulfilled'].includes(order?.status);
  const canPay = !!(
    order &&
    ['approved', 'submitted', 'fulfilled'].includes(order.status) &&
    order.payment_status !== 'paid' &&
    (isAdmin || order.user_id === user?.id)
  );

  const payNow = async () => {
    setBusy('pay');
    try {
      const { data } = await api.post('/payments/order-checkout', {
        order_id: id,
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setBusy('');
    }
  };

  const doAction = async (endpoint, key, extra) => {
    setBusy(key);
    try {
      await api.post(endpoint, extra);
      toast.success('Order updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(''); }
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/admin/orders/${id}/dj-order.csv`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${order.order_number}-dj.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch { toast.error('Could not download CSV'); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/orders/${id}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${order.order_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch { toast.error('Could not download PDF'); }
  };

  const downloadXlsx = async () => {
    try {
      const resp = await api.get(`/orders/${id}/export.xlsx`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${order.order_number}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch { toast.error('Could not download Excel'); }
  };

  if (loading) return <div className="container-x py-20 sans text-neutral-500">Loading order…</div>;
  if (!order) return <div className="container-x py-20 sans text-neutral-500">Order not found.</div>;

  const canApprove = order.status === 'pending_approval';
  const canRequestChanges = order.status === 'pending_approval';
  const canMarkSubmitted = isAdmin && order.status === 'approved';
  const canMarkFulfilled = isAdmin && order.status === 'submitted';
  const canCancel = isAdmin && !['fulfilled', 'submitted', 'cancelled'].includes(order.status);
  const canAcceptChanges = isAdmin && order.status === 'changes_requested';
  const canAdminEdit = isAdmin && ['draft', 'approved', 'submitted', 'fulfilled', 'changes_requested'].includes(order.status);
  const isAdminDirectEdit = editMode && isAdmin && order.status !== 'pending_approval';
  const cr = order.change_request;

  const startEdit = () => {
    setEditItems((order.items || []).map((i) => ({ sku: i.sku, qty: i.qty, name: i.name, brand: i.brand })));
    setChangeReason('');
    setEditMode(true);
  };
  const changeQty = (sku, q) => {
    const n = Math.max(1, parseInt(q, 10) || 1);
    setEditItems((cur) => cur.map((i) => i.sku === sku ? { ...i, qty: n } : i));
  };
  const removeLine = (sku) => setEditItems((cur) => cur.filter((i) => i.sku !== sku));
  const submitChangeRequest = async () => {
    if (!editItems.length) return toast.error('Keep at least one line');
    setBusy('changes');
    try {
      if (isAdminDirectEdit) {
        const { data } = await api.post(`/admin/orders/${id}/edit`, {
          items: editItems.map((i) => ({ sku: i.sku, qty: i.qty })),
          reason: changeReason,
        });
        toast.success('Order updated');
        if (data.warning_after_supplier) {
          toast.warning(data.warning_after_supplier, { duration: 10_000 });
        }
      } else {
        await api.post(`/orders/${id}/request-changes`, {
          items: editItems.map((i) => ({ sku: i.sku, qty: i.qty })),
          reason: changeReason,
        });
        toast.success('Change request sent — IJE HAUS will review');
      }
      setEditMode(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes');
    } finally { setBusy(''); }
  };

  return (
    <div className="container-x py-8 md:py-14 pb-24 md:pb-14" data-testid="order-detail">
      <Link
        to={isAdmin ? '/admin?tab=orders' : '/dashboard'}
        className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-2 mb-6"
        data-testid="order-back"
      >
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="min-w-0 flex-1">
          <p className="eyebrow">Order</p>
          <h1 className="h-display text-2xl md:text-4xl mt-1.5 md:mt-2 leading-tight" data-testid="order-number">{order.order_number}</h1>
          <p className="sans text-[11px] md:text-xs text-neutral-500 tracking-widest uppercase mt-1.5 md:mt-2 truncate">
            {order.business_name || order.user_email} · {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={downloadPdf}
            className="sans text-[10px] md:text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
            data-testid="order-download-pdf"
            title="Download line-sheet PDF (quote)"
          >
            <FileDown size={14} /> PDF
          </button>
          <button
            type="button"
            onClick={downloadXlsx}
            className="sans text-[10px] md:text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
            data-testid="order-download-xlsx"
            title="Download formatted Excel workbook"
          >
            <FileDown size={14} /> Excel
          </button>
          {canInvoice && (
            <button
              type="button"
              onClick={() => setInvoiceOpen(true)}
              className="sans text-[10px] md:text-xs tracking-widest uppercase text-[color:var(--ije-pink-dark)] hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
              data-testid="order-generate-invoice"
              title="Turn this order into a branded invoice PDF"
            >
              <FileText size={14} /> Invoice
            </button>
          )}
          {canPay && (
            <button
              type="button"
              onClick={payNow}
              disabled={busy === 'pay'}
              className="btn-primary h-9 text-[11px] px-3"
              data-testid="order-pay-now"
              title="Pay with ACH bank transfer or Cash App Pay"
            >
              {busy === 'pay' ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Pay {fmt(order.total)}
            </button>
          )}
          {order.payment_status === 'paid' && (
            <span className="sans text-[10px] md:text-xs tracking-widest uppercase text-green-700 inline-flex items-center gap-1.5 px-2 py-1" data-testid="order-paid-pill">
              <Check size={14} /> Paid
            </span>
          )}
          <StatusPill status={order.status} />
        </div>
      </div>

      {order.status === 'rejected' && order.rejection_reason && (
        <div className="mb-6 border border-red-200 bg-red-50/40 px-4 py-3 sans text-sm text-red-800" data-testid="order-rejection-reason">
          <strong className="tracking-widest uppercase text-[10px] block mb-1">Rejection reason</strong>
          {order.rejection_reason}
        </div>
      )}

      {isAdmin && cr && order.status === 'changes_requested' && (
        <div className="mb-6 border border-[color:var(--ije-pink)] bg-[color:var(--ije-pink-tint)]/50 px-4 py-4" data-testid="order-change-request-banner">
          <p className="eyebrow" style={{ color: 'var(--ije-pink-dark)' }}>Customer proposed changes</p>
          <p className="serif text-lg mt-1">Buyer wants these qty updates:</p>
          <ul className="mt-3 space-y-1">
            {(cr.items || []).map((it) => (
              <li key={it.sku} className="sans text-sm">
                <span className="font-mono text-xs mr-2">{it.qty}×</span>
                <span className="serif">{it.name}</span>
                <span className="text-neutral-500 text-xs ml-2">({it.sku})</span>
              </li>
            ))}
          </ul>
          {cr.reason && (
            <p className="sans text-xs text-neutral-700 mt-3 italic">&ldquo;{cr.reason}&rdquo;</p>
          )}
          <p className="sans text-xs text-neutral-500 mt-3">
            New total: <strong className="text-neutral-900">{fmt(cr.subtotal)}</strong>
          </p>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/accept-changes`, 'accept-changes')} className="btn-primary h-9" data-testid="order-accept-changes">
              {busy === 'accept-changes' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Accept changes → draft
            </button>
          </div>
        </div>
      )}

      {editMode ? (
        <div className="border border-[color:var(--ije-pink)] mb-6" data-testid="order-edit-mode">
          <div className="bg-[color:var(--ije-pink-tint)]/40 px-4 py-2 flex items-center justify-between">
            <p className="eyebrow" style={{ color: 'var(--ije-pink-dark)' }}>
              {isAdminDirectEdit ? 'Editing order (admin)' : 'Editing quantities'}
            </p>
            <button type="button" onClick={() => setEditMode(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black">Cancel</button>
          </div>
          {isAdminDirectEdit && (
            <div className="border-b border-neutral-100 p-3 space-y-2 bg-white" data-testid="order-edit-add-panel">
              <div className="relative">
                {!addQuery && <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />}
                <input
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                  placeholder="Add another product — search by name, SKU, or brand…"
                  className="ije-input h-10 w-full pl-9"
                  data-testid="order-edit-add-search"
                />
                {addSearching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-neutral-400" />}
              </div>
              {addResults.length > 0 && (
                <ul className="border border-neutral-200 max-h-64 overflow-y-auto divide-y divide-neutral-100" data-testid="order-edit-add-results">
                  {addResults.map((p) => {
                    const already = editItems.find((i) => i.sku === p.sku);
                    const raw = (p.images || [])[0] || '';
                    const src = raw && raw.startsWith('/api/images/')
                      ? `${API_BASE}${raw}${raw.includes('?') ? '&' : '?'}thumb=1`
                      : (raw && raw.startsWith('/api/')) ? `${API_BASE}${raw}` : (raw || '/ije-fallback.svg');
                    return (
                      <li key={p.sku} className="flex items-center gap-3 px-3 py-2.5">
                        <img src={src} alt="" className="w-10 h-10 object-cover border border-neutral-200 shrink-0 bg-white" referrerPolicy="no-referrer" onError={(e) => { e.target.src = '/ije-fallback.svg'; }} />
                        <div className="flex-1 min-w-0">
                          <p className="sans text-sm truncate">{p.name}</p>
                          <p className="sans text-[11px] text-neutral-500 truncate mt-0.5">
                            <span className="uppercase tracking-widest">{p.brand}</span>
                            <span className="mx-1.5">·</span>
                            <span className="font-mono">{p.sku}</span>
                          </p>
                          {p.wholesale_price ? <p className="serif text-xs text-[color:var(--ije-pink-dark)] mt-0.5">${Number(p.wholesale_price).toFixed(2)}</p> : null}
                        </div>
                        {already ? (
                          <span className="inline-flex items-center gap-1 sans text-[10px] tracking-widest uppercase px-2 py-1 border border-neutral-900 bg-neutral-900 text-white shrink-0">
                            <Check size={12} /> {already.qty} added
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addLine(p)}
                            className="btn-primary h-9 shrink-0"
                            data-testid={`order-edit-add-product-${p.sku}`}
                          >
                            <Plus size={14} /> Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-neutral-100">
            {editItems.map((it) => (
              <div key={it.sku} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="serif text-sm leading-tight">{it.name}</p>
                    <p className="sans text-[10px] text-neutral-500 tracking-widest uppercase mt-0.5 truncate">{it.brand} · {it.sku}</p>
                  </div>
                  <button type="button" onClick={() => removeLine(it.sku)} className="p-2 -m-2 text-neutral-400 active:text-red-600 shrink-0" title="Remove line" data-testid={`edit-remove-${it.sku}`}>
                    <X size={16} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <p className="sans text-[10px] tracking-widest uppercase text-neutral-500 mr-1">Qty</p>
                  <button type="button" onClick={() => changeQty(it.sku, it.qty - 1)} className="w-9 h-9 border border-neutral-200 sans text-lg active:bg-neutral-100" aria-label="Decrease">−</button>
                  <input
                    type="number" min="1" value={it.qty}
                    onChange={(e) => changeQty(it.sku, e.target.value)}
                    className="ije-input h-9 w-16 text-center"
                    data-testid={`edit-qty-${it.sku}`}
                  />
                  <button type="button" onClick={() => changeQty(it.sku, it.qty + 1)} className="w-9 h-9 border border-neutral-200 sans text-lg active:bg-neutral-100" aria-label="Increase">+</button>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <table className="hidden md:table w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {editItems.map((it) => (
                <tr key={it.sku}>
                  <td className="px-4 py-3">
                    <p className="serif">{it.name}</p>
                    <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase">{it.brand} · {it.sku}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number" min="1" value={it.qty}
                      onChange={(e) => changeQty(it.sku, e.target.value)}
                      className="ije-input h-9 w-20 text-right"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => removeLine(it.sku)} className="p-1.5 text-neutral-400 hover:text-red-600" title="Remove line">
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 space-y-3 bg-neutral-50">
            {isAdminDirectEdit && order.supplier_ref && (
              <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 sans">
                <strong className="tracking-widest uppercase text-[10px] mr-1">Heads up:</strong>
                This order has a Doc Johnson reference ({order.supplier_ref}). Your edit will save immediately — remember to sync the change with DJ manually.
              </div>
            )}
            <textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              rows={2}
              placeholder={isAdminDirectEdit ? "Reason for the edit (recorded in the order history)" : "Optional note for IJE HAUS — e.g. 'Reduce Rhino qty, add more strawberry'"}
              className="ije-input w-full p-3 resize-none"
              data-testid="change-reason"
            />
            <button type="button" onClick={submitChangeRequest} disabled={busy === 'changes'} className="btn-primary h-11 w-full md:w-auto md:h-10 md:ml-auto md:block" data-testid="order-send-changes">
              {busy === 'changes' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isAdminDirectEdit ? 'Save changes' : 'Send change request'}
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Mobile line-item cards */}
      <div className="md:hidden mb-6">
        <div className="space-y-2">
          {(order.items || []).map((it) => (
            <div key={it.sku} className="border border-neutral-200 p-3" data-testid={`order-line-mobile-${it.sku}`}>
              <p className="serif text-sm leading-tight">{it.name}</p>
              <p className="sans text-[10px] text-neutral-500 tracking-widest uppercase mt-0.5 truncate">{it.brand} · {it.sku}</p>
              <div className="mt-2 pt-2 border-t border-neutral-100 flex items-end justify-between gap-3">
                <p className="sans text-xs text-neutral-600">
                  <span className="serif text-sm text-neutral-900">{it.qty}</span>
                  <span className="mx-1.5">×</span>
                  {fmt(it.your_unit_price ?? it.unit_price)}
                </p>
                <p className="serif text-base">{fmt(it.line_total)}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-neutral-300 mt-3 pt-3 px-1">
          <p className="sans text-xs tracking-widest uppercase text-neutral-500">Subtotal</p>
          <p className="serif text-xl" data-testid="order-total-mobile">{fmt(order.total)}</p>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-neutral-200 mb-6">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Qty</th>
              <th className="text-right px-4 py-3">Unit</th>
              <th className="text-right px-4 py-3">Line</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(order.items || []).map((it) => (
              <tr key={it.sku} data-testid={`order-line-${it.sku}`}>
                <td className="px-4 py-3">
                  <p className="serif">{it.name}</p>
                  <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase">{it.brand} · {it.sku}</p>
                </td>
                <td className="px-4 py-3 text-right sans">{it.qty}</td>
                <td className="px-4 py-3 text-right serif">{fmt(it.your_unit_price ?? it.unit_price)}</td>
                <td className="px-4 py-3 text-right serif">{fmt(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-neutral-50">
              <td colSpan={3} className="px-4 py-3 text-right sans text-xs tracking-widest uppercase text-neutral-500">Subtotal</td>
              <td className="px-4 py-3 text-right serif text-lg" data-testid="order-total">{fmt(order.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      </>
      )}

      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <div>
          <p className="eyebrow">Ship to</p>
          <p className="serif mt-2 whitespace-pre-line">{order.shipping_address || <span className="text-neutral-400 italic">Not set</span>}</p>
        </div>
        <div>
          <p className="eyebrow">Notes</p>
          <p className="serif mt-2 whitespace-pre-line">{order.notes || <span className="text-neutral-400 italic">None</span>}</p>
        </div>
      </div>

      {isAdmin && vendorPOs && vendorPOs.vendors && vendorPOs.vendors.length > 0 && (
        <div className="mb-6 border border-neutral-200 p-4" data-testid="order-vendor-pos">
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <p className="eyebrow">Vendor purchase orders</p>
            <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase">
              {vendorPOs.vendor_count} vendor{vendorPOs.vendor_count === 1 ? '' : 's'} · IJE cost {fmt(vendorPOs.grand_total_cost)}
            </p>
          </div>
          <p className="sans text-xs text-neutral-500 mb-3">
            Split this order by supplier so each vendor gets only their items with IJE HAUS's cost — never the customer's wholesale.
          </p>
          <ul className="divide-y divide-neutral-100">
            {vendorPOs.vendors.map((v) => (
              <li key={v.vendor} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`vendor-po-row-${v.vendor}`}>
                <div className="min-w-0 flex-1">
                  <p className="serif text-sm truncate flex items-center gap-1.5">
                    <Package size={14} className="text-neutral-400 shrink-0" />
                    {v.vendor}
                  </p>
                  <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase mt-0.5">
                    {v.product_count} line{v.product_count === 1 ? '' : 's'} · {v.unit_count} unit{v.unit_count === 1 ? '' : 's'} · {fmt(v.total_cost)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => downloadVendorPO(v.vendor, 'pdf')}
                    className="sans text-[10px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
                    data-testid={`vendor-po-pdf-${v.vendor}`}
                    title={`Download PO PDF for ${v.vendor}`}
                  >
                    <FileDown size={14} /> PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadVendorPO(v.vendor, 'xlsx')}
                    className="sans text-[10px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
                    data-testid={`vendor-po-xlsx-${v.vendor}`}
                    title={`Download PO Excel for ${v.vendor}`}
                  >
                    <FileSpreadsheet size={14} /> Excel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isAdmin && order.admin_notes && (
        <div className="mb-6 border border-neutral-200 bg-neutral-50 p-4">
          <p className="eyebrow">Admin notes (private)</p>
          <p className="serif text-sm mt-2 whitespace-pre-line">{order.admin_notes}</p>
        </div>
      )}

      {isAdmin && order.supplier_ref && (
        <div className="mb-6 sans text-xs tracking-widest uppercase text-neutral-500">
          DJ order ref: <span className="text-neutral-900 tracking-normal font-mono normal-case">{order.supplier_ref}</span>
        </div>
      )}

      {isAdmin && invoices.length > 0 && (
        <div className="mb-6 border border-neutral-200 p-4" data-testid="order-invoices-list">
          <p className="eyebrow mb-3">Invoices</p>
          <ul className="divide-y divide-neutral-100">
            {invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="serif text-sm truncate">
                    {inv.invoice_number}
                    {inv.sent_at && (
                      <span className="sans text-[10px] tracking-widest uppercase text-green-700 ml-2">SENT</span>
                    )}
                  </p>
                  <p className="sans text-[11px] text-neutral-500 tracking-widest uppercase">
                    {inv.terms} · Due {new Date(inv.due_date).toLocaleDateString()} · {fmt(inv.total)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const resp = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: 'blob' });
                        const blobUrl = URL.createObjectURL(resp.data);
                        const a = document.createElement('a');
                        a.href = blobUrl; a.download = `${inv.invoice_number}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                      } catch { toast.error('Could not download invoice'); }
                    }}
                    className="sans text-[10px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
                    data-testid={`invoice-download-${inv.id}`}
                  >
                    <FileDown size={13} /> PDF
                  </button>
                  <button
                    type="button"
                    disabled={busy === `send-${inv.id}`}
                    onClick={async () => {
                      const label = inv.sent_at ? 'Resend this invoice to the customer?' : `Email this invoice to ${order.user_email}?`;
                      if (!window.confirm(label)) return;
                      setBusy(`send-${inv.id}`);
                      try {
                        await api.post(`/admin/invoices/${inv.id}/send`);
                        toast.success(`Invoice emailed to ${order.user_email}`);
                        loadInvoices();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Could not send invoice');
                      } finally { setBusy(''); }
                    }}
                    className="sans text-[10px] tracking-widest uppercase text-[color:var(--ije-pink-dark)] hover:text-black inline-flex items-center gap-1.5 px-2 py-1"
                    data-testid={`invoice-send-${inv.id}`}
                    title={`Email this invoice with a Pay online button to ${order.user_email}`}
                  >
                    {busy === `send-${inv.id}` ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    {inv.sent_at ? 'Resend' : 'Email invoice'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <InvoiceDialog
        order={order}
        open={invoiceOpen}
        onOpenChange={setInvoiceOpen}
        onCreated={() => loadInvoices()}
      />

      {/* Action bar — desktop inline, mobile sticky bottom */}
      {(canApprove || canMarkSubmitted || canMarkFulfilled || canCancel || canAdminEdit || (isAdmin && order.status === 'draft')) && (
        <>
        {/* Desktop inline bar */}
        <div className="hidden md:flex flex-wrap gap-3 border-t border-neutral-200 pt-6">
          {canApprove && !editMode && (
            <>
              <button type="button" disabled={!!busy} onClick={() => doAction(`/orders/${id}/approve`, 'approve')} className="btn-primary" data-testid="order-approve">
                {busy === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve order
              </button>
              {canRequestChanges && (
                <button type="button" disabled={!!busy} onClick={startEdit} className="btn-outline" data-testid="order-request-changes">
                  <Pencil size={14} /> Request qty changes
                </button>
              )}
              <button type="button" disabled={!!busy} onClick={() => setRejectOpen(true)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-red-600 px-3" data-testid="order-reject">
                <X size={14} className="inline mr-1" /> Reject
              </button>
            </>
          )}
          {canMarkSubmitted && (
            <>
              <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/push-to-dj`, 'push-dj')} className="btn-primary" data-testid="order-push-dj">
                {busy === 'push-dj' ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Push to Doc Johnson
              </button>
              <button type="button" disabled={!!busy} onClick={downloadCsv} className="btn-outline" data-testid="order-dl-csv">
                <FileDown size={14} /> Download DJ CSV
              </button>
              <button type="button" disabled={!!busy} onClick={() => {
                const ref = window.prompt('DJ order confirmation # (optional):', '');
                if (ref === null) return;
                doAction(`/admin/orders/${id}/mark-submitted`, 'submitted', { supplier_ref: ref });
              }} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-3" data-testid="order-mark-submitted">
                <Send size={14} className="inline mr-1" /> Manually mark submitted
              </button>
            </>
          )}
          {canAdminEdit && !editMode && (
            <button type="button" disabled={!!busy} onClick={startEdit} className="btn-outline" data-testid="order-admin-edit">
              <Pencil size={14} /> Edit line items
            </button>
          )}
          {canMarkFulfilled && (
            <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/mark-fulfilled`, 'fulfilled')} className="btn-primary" data-testid="order-mark-fulfilled">
              {busy === 'fulfilled' ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />} Mark fulfilled
            </button>
          )}
          {canCancel && (
            <button type="button" disabled={!!busy} onClick={() => {
              if (!window.confirm('Cancel this order?')) return;
              doAction(`/admin/orders/${id}/cancel`, 'cancel');
            }} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-red-600 px-3" data-testid="order-cancel">
              <Ban size={14} className="inline mr-1" /> Cancel order
            </button>
          )}
          {isAdmin && order.status === 'draft' && (
            <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/submit-for-approval`, 'submit')} className="btn-primary" data-testid="order-send-approval">
              {busy === 'submit' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to customer for approval
            </button>
          )}
          {isAdmin && ['draft', 'pending_approval', 'changes_requested', 'rejected'].includes(order.status) && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() => {
                if (!window.confirm('Force-approve this order without customer sign-off?\nUse this when you already have verbal / email agreement.')) return;
                doAction(`/admin/orders/${id}/force-approve`, 'force-approve');
              }}
              className="btn-outline"
              data-testid="order-force-approve"
              title="Skip customer approval — you'll be recorded as the approver"
            >
              {busy === 'force-approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Force approve
            </button>
          )}
        </div>

        {/* Mobile sticky bottom bar */}
        {!editMode && (
          <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white border-t border-neutral-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-[0_-8px_20px_rgba(0,0,0,0.04)]" data-testid="order-actions-mobile">
            {canApprove && (
              <div className="space-y-2">
                <button type="button" disabled={!!busy} onClick={() => doAction(`/orders/${id}/approve`, 'approve')} className="btn-primary h-12 w-full text-sm" data-testid="order-approve-mobile">
                  {busy === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} />} Approve · {fmt(order.total)}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  {canRequestChanges && (
                    <button type="button" disabled={!!busy} onClick={startEdit} className="btn-outline h-11 text-xs" data-testid="order-request-changes-mobile">
                      <Pencil size={14} /> Edit qty
                    </button>
                  )}
                  <button type="button" disabled={!!busy} onClick={() => setRejectOpen(true)} className="btn-outline h-11 text-xs text-neutral-500" data-testid="order-reject-mobile">
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            )}
            {canMarkSubmitted && (
              <div className="space-y-2">
                <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/push-to-dj`, 'push-dj')} className="btn-primary h-12 w-full text-sm" data-testid="order-push-dj-mobile">
                  {busy === 'push-dj' ? <Loader2 size={14} className="animate-spin" /> : <Zap size={16} />} Push to Doc Johnson
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" disabled={!!busy} onClick={downloadCsv} className="btn-outline h-11 text-xs" data-testid="order-dl-csv-mobile">
                    <FileDown size={14} /> DJ CSV
                  </button>
                  <button type="button" disabled={!!busy} onClick={() => {
                    const ref = window.prompt('DJ order confirmation # (optional):', '');
                    if (ref === null) return;
                    doAction(`/admin/orders/${id}/mark-submitted`, 'submitted', { supplier_ref: ref });
                  }} className="btn-outline h-11 text-xs text-neutral-500" data-testid="order-mark-submitted-mobile">
                    <Send size={14} /> Manual
                  </button>
                </div>
              </div>
            )}
            {canMarkFulfilled && (
              <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/mark-fulfilled`, 'fulfilled')} className="btn-primary h-12 w-full text-sm" data-testid="order-mark-fulfilled-mobile">
                {busy === 'fulfilled' ? <Loader2 size={14} className="animate-spin" /> : <Truck size={16} />} Mark fulfilled
              </button>
            )}
            {isAdmin && order.status === 'draft' && (
              <button type="button" disabled={!!busy} onClick={() => doAction(`/admin/orders/${id}/submit-for-approval`, 'submit')} className="btn-primary h-12 w-full text-sm" data-testid="order-send-approval-mobile">
                {busy === 'submit' ? <Loader2 size={14} className="animate-spin" /> : <Send size={16} />} Send to customer for approval
              </button>
            )}
            {canAdminEdit && !editMode && (
              <button
                type="button"
                disabled={!!busy}
                onClick={startEdit}
                className={`${canApprove || canMarkSubmitted || canMarkFulfilled || (isAdmin && order.status === 'draft') ? 'btn-outline h-11 w-full text-xs mt-2' : 'btn-primary h-12 w-full text-sm'}`}
                data-testid="order-admin-edit-mobile"
              >
                <Pencil size={14} /> Edit line items
              </button>
            )}
            {!canApprove && !canMarkSubmitted && !canMarkFulfilled && !(isAdmin && order.status === 'draft') && !canAdminEdit && canCancel && (
              <button type="button" disabled={!!busy} onClick={() => {
                if (!window.confirm('Cancel this order?')) return;
                doAction(`/admin/orders/${id}/cancel`, 'cancel');
              }} className="btn-outline h-11 w-full text-xs text-neutral-500" data-testid="order-cancel-mobile">
                <Ban size={14} /> Cancel order
              </button>
            )}
          </div>
        )}
        </>
      )}

      {rejectOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50 px-4 pb-4 md:pb-0" data-testid="reject-modal">
          <div className="bg-white border border-neutral-200 w-full max-w-md p-6">
            <p className="eyebrow">Reject order</p>
            <p className="serif text-xl mt-2">Tell us what&rsquo;s off?</p>
            <textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="ije-input w-full p-3 resize-none mt-4"
              placeholder="e.g. wrong quantities, need to adjust shipping…"
              data-testid="reject-reason"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setRejectOpen(false)} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-10">Cancel</button>
              <button
                type="button"
                onClick={async () => {
                  await doAction(`/orders/${id}/reject`, 'reject', { reason: rejectReason });
                  setRejectOpen(false); setRejectReason('');
                }}
                disabled={busy === 'reject'}
                className="btn-primary h-10"
                data-testid="reject-confirm"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
